import type { GetAuthQuery } from "@/graphql/common/common.hooks";
import type { AuthUser } from "@/types/auth";

import { useReactiveVar } from "@apollo/client/react";

import { BASE_URL } from "@/constants";
import { IS_ME_LINK, LANDING_URL } from "@/constants/app";
import { currentUserVar } from "@/graphql/globalVar/state";
import i18n from "@/i18n";

const getAuthDetails = (user: AuthUser | null) => ({
  id: user?.id,
  username: user?.username || "",
  isAuthenticated: !!user,
  isAdmin: user?.isAdmin || false,
  isPremium: !!user?.premiumSubscription?.id,
  isVerified: user?.isVerified || false,
  hasLite:
    !!user?.liteSubscriptions?.length && user.liteSubscriptions.length > 0,
  userRoles: user?.roles || [],
  isSwitchedUser: user?.isSwitchedUser || false,
  fullName: user?.fullName || "",
  undownloadedFolderReports: user?.undownloadedFolderReports || [],
});

// Auth Service for handling user authentication
export const authService = {
  setUser: (data: GetAuthQuery) => {
    if (!data?.user) return;
    currentUserVar({
      ...data.user,
      isSwitchedUser: data.app?.isSwitchedUser ?? false,
    });
  },
  getUser: () => getAuthDetails(currentUserVar()),
  setUndownloadedFolderReports: (
    undownloadedFolderReports: AuthUser["undownloadedFolderReports"]
  ) => {
    const user = currentUserVar();
    if (!user) return;
    currentUserVar({ ...user, undownloadedFolderReports });
  },
  logout: () => {
    currentUserVar(null);
    const baseUrl = IS_ME_LINK ? LANDING_URL : BASE_URL;
    const isLocalhost = window.location.hostname === "localhost";
    const cookieDomain = isLocalhost
      ? ""
      : `domain=.${new URL(baseUrl).hostname}; `;
    document.cookie = `folderId=; ${cookieDomain}path=/; max-age=0;`;
    const locale = i18n.language === "en" ? "" : `/${i18n.language}`;
    window.location.href = `${baseUrl}${locale}/logout`;
  },
};

// Auth hook for handling user authentication
export const useAuthService = () => {
  const user = useReactiveVar(currentUserVar);
  return getAuthDetails(user);
};
